import React from 'react';
import { motion } from 'framer-motion';

import wineImage from '../../assets/home-about-image.png';
import bottleImage from '../../assets/wine-bottle.png';
import iconImg1 from '../../assets/2.png';
import iconImg2 from '../../assets/3.png';
import iconImg3 from '../../assets/4.png';
import iconImg4 from '../../assets/5.png';
import grapesSketchImg from '../../assets/grapes-sketch.png';
import wineGlassSketch from '../../assets/wine-glass-sketch.png';
import { useNavigate } from 'react-router-dom';

// Animation Variants
const featureVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: {
            delay: i * 0.2,
            duration: 0.5,
            ease: 'easeOut',
        },
    }),
};

const HomeAboutCarneiro = () => {
    const navigate = useNavigate()

    return (
        <section className="relative bg-[--neutral-white] lg:min-h-screen py-12 px-4 lg:px-12 overflow-hidden">
            <div className="flex flex-col lg:flex-row items-center justify-between relative z-10 gap-6">

                {/* Left Image */}
                <div className="w-full lg:w-1/2 relative">
                    <motion.img
                        src={wineImage}
                        alt="Celebration"
                        className="rounded-lg shadow-xl w-[450px]"
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    />

                    {/* ABV Info Box */}
                    <motion.div
                        className="absolute bottom-4 left-4 bg-green-800 text-white px-6 py-4 rounded-sm text-sm shadow-md"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.6, duration: 0.6 }}
                    >
                        <div className="flex gap-6">
                            <div>
                                <p className="text-xl font-bold">13% ABV</p>
                                <p className="text-xs">Red Reserve</p>
                            </div>
                            <div>
                                <p className="text-xl font-bold">11% ABV</p>
                                <p className="text-xs">Blush Rosé</p>
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Right Content */}
                <div className="w-full lg:w-1/2 z-10">
                    <motion.h2
                        className="text-4xl sm:text-5xl font-extrabold text-gray-900 leading-tight"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                    >
                        BEST WINERY IN <br />
                        YOUR <span className="relative inline-block">
                            CITY AREA
                            <span className="absolute bottom-0 left-0 w-full h-2 bg-yellow-400 -z-10"></span>
                        </span>
                    </motion.h2>

                    <motion.p
                        className="mt-4 text-gray-700 font-medium"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2, duration: 0.6 }}
                    >
                        Carneiro Wine is not just a beverage — it's an experience rooted in craftsmanship,
                        tradition, and the richness of our soil. Every bottle tells a story.
                    </motion.p>

                    <motion.p
                        className="mt-2 text-gray-600 text-sm leading-relaxed"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.4, duration: 0.6 }}
                    >
                        Established in the heart of wine country, we pride ourselves on handpicking
                        organic grapes, embracing sustainable fermentation, and aging our wines in
                        French oak barrels for unmatched depth and character.
                    </motion.p>

                    {/* Animated Features */}
                    <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
                        {[
                            { icon: iconImg1, label: "Craft Red Wines" },
                            { icon: iconImg2, label: "Red & Vanilla" },
                            { icon: iconImg3, label: "Organic Grapes" },
                            { icon: iconImg4, label: "Fruit Essence" },
                        ].map((item, index) => (
                            <motion.div
                                key={index}
                                custom={index}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.5 }}
                                variants={featureVariants}
                                className="flex items-center gap-3"
                            >
                                <img src={item.icon} alt={item.label} className="w-10 h-10 rounded-full shadow-md" />
                                <span className="text-gray-800 font-medium">{item.label}</span>
                            </motion.div>
                        ))}
                    </div>

                    {/* Button */}
                    <div
                        className="mt-8"
                        onClick={() => {
                            console.log("Navigating to /about");
                            navigate("/about");
                        }}
                    >
                        <button className="px-6 py-2 bg-green-800 text-white font-bold uppercase tracking-widest hover:bg-green-700 transition" >
                            Read More
                        </button>
                    </div>
                </div>
            </div>

            {/* Center Bottle */}
            <div
                className="hidden lg:block absolute left-[280px] top-[35%] -translate-y-[40%] z-20"
            >
                <img src={bottleImage} alt="Carneiro Wine Bottle" className="h-[600px] drop-shadow-2xl" />
            </div>

            {/* Decorative Sketches */}
            <motion.div
                className='absolute top-0 -left-10 opacity-20'
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 0.2, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
            >
                <img src={grapesSketchImg} alt="" className='w-[200px]' />
            </motion.div>

            <motion.div
                className='absolute -bottom-4 -right-4 opacity-5'
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 0.05, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
            >
                <img src={wineGlassSketch} alt="" className='w-[200px]' />
            </motion.div>
        </section>
    );
};

export default HomeAboutCarneiro;
